<template>
  <div class="app-topbar">
    <div class="app-topbar__inner">
      <header class="app-topbar__header">
        <div class="brand-block">
          <div class="brand-mark">
            <img class="brand-mark__image" src="/logo.png" alt="格纳斯合同风险自查助手" />
          </div>
          <div class="brand-block__copy">
            <p class="shell__eyebrow">企业合同风控平台</p>
            <h1>格纳斯合同风险自查助手</h1>
          </div>
        </div>

        <nav class="shell__nav">
          <RouterLink :class="navLinkClass('home')" to="/">产品首页</RouterLink>
          <RouterLink :class="navLinkClass('pricing')" to="/pricing">套餐定价</RouterLink>
          <RouterLink v-if="currentUser" :class="navLinkClass('review')" to="/review-center">审查中心</RouterLink>
          <RouterLink v-if="currentUser" :class="navLinkClass('user-center')" to="/user-center">用户中心</RouterLink>
        </nav>

        <div class="auth-block">
          <template v-if="currentUser">
            <el-dropdown trigger="click" placement="bottom-end" popper-class="user-menu-popper">
              <button class="user-menu-trigger" type="button">
                <span class="user-menu-trigger__avatar">{{ currentUser.username.slice(0, 1).toUpperCase() }}</span>
                <span class="user-menu-trigger__content">
                  <span class="user-menu-trigger__name">{{ currentUser.username }}</span>
                </span>
                <span class="user-menu-trigger__caret">▾</span>
              </button>
              <template #dropdown>
                <el-dropdown-menu>
                  <div class="user-menu-card">
                    <div class="user-menu-card__top">
                      <div class="user-menu-card__avatar">{{ currentUser.username.slice(0, 1).toUpperCase() }}</div>
                      <div class="user-menu-card__identity">
                        <strong>{{ currentUser.username }}</strong>
                        <p>{{ currentUser.email }}</p>
                      </div>
                    </div>
                    <div class="user-menu-card__section">
                      <span class="user-menu-card__section-title">账户信息</span>
                      <div class="user-menu-card__meta">
                        <span>账户类型</span>
                        <strong>{{ memberLabel }}</strong>
                      </div>
                      <div class="user-menu-card__meta">
                        <span>当前状态</span>
                        <strong>审查权限已启用</strong>
                      </div>
                    </div>
                  </div>
                  <el-dropdown-item divided @click="$emit('logout')">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
          <template v-else>
            <el-button type="primary" @click="$emit('open-auth')">进入工作台</el-button>
          </template>
        </div>
      </header>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import type { CurrentUser } from '../api/client'

const route = useRoute()

const props = defineProps<{
  currentUser: CurrentUser | null
  memberLabel: string
}>()

defineEmits<{
  logout: []
  'open-auth': []
}>()

const activeNavKey = computed(() => {
  if (route.path === '/review-center' || route.path === '/review') {
    return 'review'
  }

  if (route.path === '/pricing') {
    return 'pricing'
  }

  if (route.path === '/user-center') {
    return 'user-center'
  }

  return 'home'
})

function navLinkClass(key: 'home' | 'pricing' | 'review' | 'user-center') {
  return [
    'shell__nav-link',
    props.currentUser && (key === 'review' || key === 'user-center') ? 'shell__nav-link--secondary' : 'shell__nav-link--primary',
    { 'shell__nav-link--active': activeNavKey.value === key },
  ]
}
</script>

<style scoped>
.app-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  background: linear-gradient(180deg, rgba(239, 245, 255, 0.94) 0%, rgba(239, 245, 255, 0.82) 72%, rgba(239, 245, 255, 0) 100%);
}

.app-topbar__inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 12px 12px 0;
}

.app-topbar__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(238, 246, 255, 0.88) 54%, rgba(235, 250, 255, 0.82) 100%);
  backdrop-filter: blur(18px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}


.brand-block {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  justify-self: start;
  width: 100%;
}

.brand-block__copy {
  min-width: 0;
}

.brand-mark {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 12px 24px rgba(37, 99, 255, 0.24);
}

.brand-mark__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.shell__eyebrow {
  margin: 0;
  font-size: 10px;
  color: #0891b2;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.brand-block h1 {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
}

.shell__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  min-width: 0;
  justify-self: center;
}

.shell__nav-link {
  flex: 0 0 auto;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #4d5c76;
  white-space: nowrap;
  border: 1px solid transparent;
  transition: all 0.18s ease;
}

.shell__nav-link:hover {
  color: #0d2d75;
  border-color: rgba(96, 165, 250, 0.2);
  background: rgba(255, 255, 255, 0.52);
}

.shell__nav-link--active {
  color: #0d2d75;
  background: linear-gradient(135deg, rgba(37, 99, 255, 0.12) 0%, rgba(34, 211, 238, 0.14) 100%);
  border-color: rgba(37, 99, 255, 0.2);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.auth-block {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
}

.user-menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 12px 6px 6px;
  border: 1px solid rgba(96, 165, 250, 0.18);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.76);
  color: #0f172a;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
  backdrop-filter: blur(14px);
}

.user-menu-trigger:hover {
  border-color: rgba(34, 211, 238, 0.3);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
}

.user-menu-trigger__avatar,
.user-menu-card__avatar {
  width: 32px;
  height: 32px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #1d4ed8 0%, #2563ff 50%, #22d3ee 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 10px 22px rgba(37, 99, 255, 0.22);
}

.user-menu-trigger__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.user-menu-trigger__name {
  font-size: 13px;
  font-weight: 700;
}

.user-menu-trigger__caret {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1;
}

:deep(.user-menu-popper.el-popper) {
  border: 1px solid rgba(125, 211, 252, 0.26);
  border-radius: 16px;
  box-shadow: 0 24px 46px rgba(15, 23, 42, 0.14);
}

:deep(.user-menu-popper .el-dropdown-menu) {
  padding: 8px;
  border-radius: 16px;
}

:deep(.user-menu-popper .el-dropdown-menu__item) {
  border-radius: 10px;
  margin-top: 4px;
  font-weight: 600;
}

:deep(.user-menu-popper .el-dropdown-menu__item:hover) {
  background: linear-gradient(135deg, rgba(37, 99, 255, 0.08) 0%, rgba(34, 211, 238, 0.12) 100%);
  color: #1d4ed8;
}

.user-menu-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 256px;
  padding: 6px 6px 2px;
}

.user-menu-card__top {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 4px 0;
}

.user-menu-card__identity {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.user-menu-card__top strong {
  display: block;
  font-size: 14px;
  color: #0f172a;
}

.user-menu-card__top p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.user-menu-card__section {
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.user-menu-card__section-title {
  display: block;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
}

.user-menu-card__meta {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-top: 10px;
  border-top: 1px solid rgba(226, 232, 240, 0.9);
}

.user-menu-card__meta:first-of-type {
  padding-top: 0;
  border-top: none;
}

@media (max-width: 980px) {
  .app-topbar__header {
    display: flex;
    flex-wrap: wrap;
    row-gap: 10px;
  }

  .shell__nav {
    justify-content: flex-start;
    width: 100%;
    order: 3;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .shell__nav::-webkit-scrollbar {
    display: none;
  }

  .auth-block {
    margin-left: auto;
  }
}

@media (max-width: 720px) {
  .app-topbar__inner {
    padding: 10px 10px 0;
  }

  .app-topbar__header {
    padding: 8px 10px;
  }

  .brand-block {
    flex: 1 1 0;
    max-width: calc(100% - 84px);
  }

  .shell__eyebrow {
    display: none;
  }

  .brand-block h1 {
    margin-top: 0;
    font-size: 13px;
  }

  .user-menu-trigger {
    gap: 6px;
    min-height: 38px;
    padding: 4px 8px 4px 4px;
    border-radius: 12px;
  }

  .user-menu-trigger__content {
    display: none;
  }

  .shell__nav-link--secondary {
    opacity: 0.88;
  }
}

@media (max-width: 560px) {

  .brand-block {
    max-width: calc(100% - 72px);
  }

  .brand-mark {
    width: 30px;
    height: 30px;
    border-radius: 9px;
  }

  .shell__nav-link {
    padding: 5px 9px;
    font-size: 12px;
  }

  .auth-block :deep(.el-button) {
    min-height: 38px;
    padding: 0 12px;
    border-radius: 10px;
  }
}
</style>